/* Kindra gallery: independent of Impact's variant picker and carousel internals. */
(() => {
  if (customElements.get('kindra-elata-gallery')) return;

  class KindraElataGallery extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.track = this.querySelector('[data-keg-track]');
      this.items = Array.from(this.querySelectorAll('[data-keg-item]'));
      this.dots = Array.from(this.querySelectorAll('[data-keg-dot]'));
      this.counter = this.querySelector('[data-keg-counter]');
      this.desktopQuery = window.matchMedia('(min-width: 1000px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.track) return;

      this.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-keg-dot]');
        if (dot) this.selectMedia(dot.dataset.mediaId);
        else {
          const play = event.target.closest('[data-keg-activate]');
          if (play) this.activateExternal(play.closest('[data-keg-external]'));
        }
      }, { signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || this.desktopQuery.matches) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, { signal });
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame || this.desktopQuery.matches) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          const trackBounds = this.track.getBoundingClientRect();
          const nearest = this.visibleItems.reduce((best, item, index) => {
            const distance = Math.abs(item.getBoundingClientRect().left - trackBounds.left);
            return distance < best.distance ? { index, distance } : best;
          }, { index: 0, distance: Infinity });
          this.updateActive(nearest.index);
        });
      }, { passive: true, signal });
      this.addEventListener('load', () => this.updateHeight(), { capture: true, signal });
      this.addEventListener('loadedmetadata', () => this.updateHeight(), { capture: true, signal });
      window.addEventListener('resize', () => this.updateHeight(), { passive: true, signal });
      this.desktopQuery.addEventListener('change', () => {
        this.updateResponsiveMedia();
        this.track.setAttribute('tabindex', this.desktopQuery.matches ? '-1' : '0');
        this.updateSlideRoles();
        this.goTo(this.index, false);
      }, { signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.items.forEach((item) => this.resizeObserver.observe(item));
      }
      this.setAttribute('data-enhanced', '');
      this.track.setAttribute('tabindex', this.desktopQuery.matches ? '-1' : '0');
      this.setFinish(this.finish || this.dataset.initialFinish, this.featuredMediaId || this.dataset.featuredMediaId);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.positionFrame);
      this.scrollFrame = null;
      this.positionFrame = null;
      this.items?.forEach((item) => this.pauseItem(item));
      this.finishRequest = (this.finishRequest || 0) + 1;
      this.finishAnimation?.cancel();
    }

    normaliseFinish(finish) {
      const value = String(finish || 'natural').trim().toLowerCase();
      return value === 'black' ? 'ash edition' : value;
    }

    async setFinish(finish, featuredMediaId, heater = this.heater || 'harvia') {
      const previousFinish = this.displayedFinish;
      const previousHeater = this.displayedHeater;
      this.heater = heater;
      const previousIndex = this.index || 0;
      this.finish = this.normaliseFinish(finish);
      this.featuredMediaId = featuredMediaId ? String(featuredMediaId) : '';
      if (!this.items) return;
      const base = this.items.filter(item => !item.dataset.featurePosition && (item.dataset.finish === 'all' || item.dataset.finish === this.finish));
      const previewLimit = Number(this.dataset?.previewLimit) || base.length;
      const matching = this.resolveFeatureSlots(base).slice(0, previewLimit);
      const request = this.finishRequest = (this.finishRequest || 0) + 1;
      this.finishAnimation?.cancel();
      const changing = !!this.visibleItems?.length && (previousFinish !== this.finish || previousHeater !== this.heater);
      const index = changing ? Math.min(previousIndex, matching.length - 1) : Math.max(0, matching.findIndex(item => item.dataset.mediaId === this.featuredMediaId));
      if (changing) {
        // Keep the old image visible until the new angle is ready, so slow
        // connections never produce a blank gallery between finishes.
        const images = (this.desktopQuery.matches ? matching.slice(0, 2) : [matching[index]]).flatMap(item => Array.from(item?.querySelectorAll('img') || []));
        await Promise.all(images.map(image => { image.loading = 'eager'; return image.decode?.().catch(() => {}); }));
        if (!this.isConnected || request !== this.finishRequest) return;
        if (!this.reducedMotion.matches) {
          this.finishAnimation = this.track.animate({ opacity: [1, 0] }, { duration: 80, easing: 'ease-out', fill: 'forwards' });
          await this.finishAnimation.finished.catch(() => {});
          if (!this.isConnected || request !== this.finishRequest) return;
        }
      }
      this.visibleItems = matching;
      this.displayedFinish = this.finish;
      this.displayedHeater = this.heater;
      matching.forEach((item, i) => {
        if (item !== base[i]) this.track.insertBefore(item, base[i]);
      });
      const visibleIds = new Set(this.visibleItems.map((item) => item.dataset.mediaId));
      this.items.forEach((item) => {
        item.hidden = !visibleIds.has(item.dataset.mediaId);
        if (item.hidden) this.pauseItem(item);
      });
      this.visibleItems.forEach((item) => {
        const wide = item.dataset.wideOverride === 'true';
        item.toggleAttribute('data-wide', wide);
        item.querySelectorAll('[data-keg-original] img').forEach(image => {
          image.sizes = `(min-width: 1000px) ${wide ? '65' : '32'}vw, 100vw`;
        });
      });
      this.updateResponsiveMedia();
      this.updateSlideRoles();
      this.dots.forEach((dot) => {
        dot.hidden = !visibleIds.has(dot.dataset.mediaId);
        const index = this.visibleItems.findIndex((item) => item.dataset.mediaId === dot.dataset.mediaId);
        if (index >= 0) dot.setAttribute('aria-label', `View product media ${index + 1} of ${this.visibleItems.length}`);
      });
      // Feature dots follow the replaced slot, not the block's markup order.
      this.visibleItems.forEach(item => {
        const dot = this.dots.find(dot => dot.dataset.mediaId === item.dataset.mediaId);
        if (dot) dot.parentElement.appendChild(dot);
      });
      const multiple = this.visibleItems.length > 1;
      this.querySelector('[data-keg-dots]').hidden = !multiple;
      this.goTo(index, false);
      this.finishAnimation?.cancel();
      if (changing && !this.reducedMotion.matches) this.finishAnimation = this.track.animate({ opacity: [0, 1] }, { duration: 140, easing: 'ease-out' });
    }

    updateSlideRoles() {
      this.visibleItems?.forEach((item, index) => {
        if (this.desktopQuery.matches) item.removeAttribute('aria-roledescription');
        else item.setAttribute('aria-roledescription', 'slide');
        item.setAttribute('aria-label', `${index + 1} of ${this.visibleItems.length}`);
      });
    }

    resolveFeatureSlots(base) {
      return base.map((original, index) => {
        const candidates = this.items.filter(item =>
          Number(item.dataset.featurePosition) === index + 1 &&
          (item.dataset.finish === 'all' || item.dataset.finish === this.finish) &&
          (!item.dataset.heater || item.dataset.heater === 'all' || item.dataset.heater === this.heater)
        );
        // Most specific match wins; equal specificity preserves editor order.
        const specificity = item => Number(item.dataset.finish === this.finish) +
          Number(!!item.dataset.heater && item.dataset.heater !== 'all' && item.dataset.heater === this.heater);
        return candidates.reduce((best, item) => !best || specificity(item) > specificity(best) ? item : best, null) || original;
      });
    }

    updateResponsiveMedia() {
      this.items.forEach(item => {
        const template = item.querySelector('[data-keg-mobile-template]');
        if (!template) return;
        const useMobile = !this.desktopQuery.matches && !item.hidden;
        let mobile = item.querySelector('[data-keg-mobile]');
        if (useMobile && !mobile) {
          item.appendChild(template.content.cloneNode(true));
          mobile = item.querySelector('[data-keg-mobile]');
        }
        const original = item.querySelector('[data-keg-original]');
        if (original.hidden !== useMobile) this.pauseItem(item);
        original.hidden = useMobile;
        if (mobile) mobile.hidden = !useMobile;
      });
      if (this.visibleItems?.length) this.updateActive(this.index || 0);
    }

    selectMedia(mediaId) {
      const index = this.visibleItems.findIndex((item) => item.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    goTo(index, smooth = true) {
      if (!this.visibleItems?.length) return;
      const next = Math.max(0, Math.min(index || 0, this.visibleItems.length - 1));
      this.updateActive(next);
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = null;
        if (!this.isConnected || this.desktopQuery.matches) return;
        const item = this.visibleItems[next];
        const distance = item.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollTo({ left: this.track.scrollLeft + distance, behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'instant' });
      });
    }

    updateActive(index) {
      const previousItem = this.visibleItems?.[this.index];
      this.index = index;
      const active = this.visibleItems?.[index];
      if (!active) return;
      if (previousItem && previousItem !== active && !this.desktopQuery.matches) this.pauseItem(previousItem);
      const count = this.visibleItems.length;
      const label = `${String(index + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`;
      if (this.counter && this.counter.textContent !== label) this.counter.textContent = label;
      this.dataset.activeMediaType = (!this.desktopQuery.matches && active.querySelector('[data-keg-mobile]:not([hidden])')?.dataset.mediaType) || active.dataset.mediaType;
      this.dots.forEach((dot) => dot.setAttribute('aria-current', String(!dot.hidden && dot.dataset.mediaId === active.dataset.mediaId)));
      this.updateHeight();
    }

    updateHeight() {
      if (!this.track) return;
      if (this.desktopQuery.matches) {
        this.style.removeProperty('--keg-active-height');
        return;
      }
      const active = this.visibleItems?.[this.index];
      if (active?.offsetHeight) this.style.setProperty('--keg-active-height', `${active.offsetHeight}px`);
    }

    activateExternal(container) {
      if (!container || container.querySelector('iframe')) return;
      container.querySelector('[data-keg-poster]').hidden = true;
      container.appendChild(container.querySelector('template').content.cloneNode(true));
      container.querySelector('iframe')?.focus();
    }

    pauseItem(item) {
      item.querySelectorAll('video').forEach((video) => video.pause());
      item.querySelectorAll('[data-keg-external]').forEach((container) => {
        container.querySelector('iframe')?.remove();
        container.querySelector('[data-keg-poster]').hidden = false;
      });
      item.querySelectorAll('model-viewer').forEach((model) => {
        if (typeof model.pause === 'function') model.pause();
      });
    }
  }

  customElements.define('kindra-elata-gallery', KindraElataGallery);
})();
